import { createBrowserHistory, createPath, parsePath } from 'history';
import { getCustomDomainSlug } from './portalSlug';

/** Router path (/atpp/login) from a browser path (/login) on a custom domain. */
export const withPortalSlug = (slug, pathname) => {
  const prefix = `/${slug}`;
  const path = pathname || '/';
  if (path === prefix || path.startsWith(`${prefix}/`)) {
    return path;
  }
  return path === '/' ? prefix : `${prefix}${path}`;
};

/** Browser path (/login) from a router path (/atpp/login) on a custom domain. */
export const withoutPortalSlug = (slug, pathname) => {
  const prefix = `/${slug}`;
  if (pathname === prefix) {
    return '/';
  }
  if (pathname && pathname.startsWith(`${prefix}/`)) {
    return pathname.slice(prefix.length);
  }
  return pathname;
};

/**
 * Wraps a history so routes keep their /:orgName prefix while the browser URL
 * on a custom domain stays path-less. Relative paths resolve against the
 * browser location, so only absolute pathnames are rewritten.
 */
export const createPortalHistory = (slug, base = createBrowserHistory()) => {
  if (!slug) {
    return base;
  }

  const toBrowser = (to) => {
    if (typeof to === 'string') {
      if (!to.startsWith('/')) {
        return to;
      }
      const parsed = parsePath(to);
      return createPath({
        ...parsed,
        pathname: withoutPortalSlug(slug, parsed.pathname),
      });
    }
    if (to && typeof to.pathname === 'string' && to.pathname.startsWith('/')) {
      return { ...to, pathname: withoutPortalSlug(slug, to.pathname) };
    }
    return to;
  };

  let lastBrowserLocation;
  let lastRouterLocation;
  const toRouter = (location) => {
    if (location !== lastBrowserLocation) {
      lastBrowserLocation = location;
      lastRouterLocation = {
        ...location,
        pathname: withPortalSlug(slug, location.pathname),
      };
    }
    return lastRouterLocation;
  };

  const browserPath = base.location.pathname;
  const canonicalPath = withoutPortalSlug(slug, browserPath);
  if (canonicalPath !== browserPath) {
    base.replace({ ...base.location, pathname: canonicalPath });
  }

  return {
    get length() {
      return base.length;
    },
    get action() {
      return base.action;
    },
    get location() {
      return toRouter(base.location);
    },
    createHref: (location) => base.createHref(toBrowser(location)),
    push: (to, state) => base.push(toBrowser(to), state),
    replace: (to, state) => base.replace(toBrowser(to), state),
    go: (n) => base.go(n),
    goBack: () => base.goBack(),
    goForward: () => base.goForward(),
    block: (prompt) =>
      base.block(
        typeof prompt === 'function'
          ? (location, action) => prompt(toRouter(location), action)
          : prompt
      ),
    listen: (listener) =>
      base.listen((location, action) => listener(toRouter(location), action)),
  };
};

const portalHistory = createPortalHistory(getCustomDomainSlug());

export default portalHistory;
